/*
 * overview.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Chip,
  Collapse,
  Grid2,
  Backdrop,
  Button,
  CircularProgress,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import {
  ExpandMore,
  ExpandLess,
  OpenInNew,
} from "@mui/icons-material";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";

import Moment from "moment";

import Topheader from "../../../Layout/header/header";
import APIManager from "../../../Services/ApiManager/ApiManager";
import PopupDialog from "../../../Components/PopupDialog";
import ApplicationQaReportsPopUp from "../../../Components/ApplicationQaReportsPopUp/ApplicationQaReportsPopUp";
import "./overview.css";
import { getConfig } from "../../../config";

// Inline helper component for loading backdrop
const LoadingBackdrop = ({ open }) => (
  <Backdrop
    open={open}
    sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
  >
    <CircularProgress color="inherit" />
  </Backdrop>
);

LoadingBackdrop.propTypes = {
  open: PropTypes.bool,
};

// Inline helper component for status badge
const StatusBadge = ({ status }) => (
  <Typography
    sx={{
      fontSize: "12px",
      width: "max-content",
      color: "rgba(0, 86, 45, 1))",
      backgroundColor: "rgba(152, 240, 200, 1)",
      borderRadius: "4px",
      padding: "5px 8px 5px 8px",
      fontWeight: "600",
    }}
  >
    {status}
  </Typography>
);

StatusBadge.propTypes = {
  status: PropTypes.string,
};

const ExpandCollapseIcon = ({ isExpanded }) => (
  <svg
    width="8"
    height="10"
    viewBox="0 0 8 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d={
        isExpanded
          ? "M4 0.666585L0 4.66658L0.933333 5.59992L3.33333 3.19992V9.33325H4.66667V3.19992L7.06667 5.59992L8 4.66658L4 0.666585Z"
          : "M4 9.33342L0 5.33342L0.933333 4.40008L3.33333 6.80008V0.666748H4.66667V6.80008L7.06667 4.40008L8 5.33342L4 9.33342Z"
      }
      fill="#0476E2"
    />
  </svg>
);

ExpandCollapseIcon.propTypes = {
  isExpanded: PropTypes.bool,
};

const TargetedItemsSection = ({
  title,
  items,
  visibleItems,
  handleShowAll,
  showAll,
  itemType,
}) => (
  <Box mb={3}>
    <Typography variant="body2" fontWeight={600} mb={1}>
      {title}
    </Typography>
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {visibleItems.map((item) => (
        <Chip
          sx={{ borderRadius: "2px" }}
          key={item}
          label={item}
          size="small"
        />
      ))}
      {!showAll && items.length > 14 && (
        <Button
          className="customButton"
          size="small"
          onClick={() => handleShowAll(true)}
          sx={
            title === "Targeted Countries"
              ? {
                  height: "24px",
                  width: "100px",
                  textTransform: "none",
                  color: "rgba(4, 118, 226, 1)",
                  border: "1px solid rgba(4, 118, 226, 1)",
                  borderRadius: "4px",
                  display: "flex",
                  justifyContent: "space-around",
                }
              : undefined
          }
        >
          {items.length - 14} {" More"}{" "}
          <ExpandCollapseIcon isExpanded={false} />
        </Button>
      )}
      {showAll && items.length > 14 && (
        <Button
          className="customButton"
          size="small"
          onClick={() => handleShowAll(false)}
          sx={
            title === "Targeted Countries"
              ? {
                  height: "24px",
                  width: "100px",
                  textTransform: "none",
                  color: "rgba(4, 118, 226, 1)",
                  border: "1px solid rgba(4, 118, 226, 1)",
                  borderRadius: "4px",
                  display: "flex",
                  justifyContent: "space-around",
                }
              : undefined
          }
        >
          {"Collapse"} <ExpandCollapseIcon isExpanded={true} />
        </Button>
      )}
    </Stack>
  </Box>
);

TargetedItemsSection.propTypes = {
  title: PropTypes.string,
  items: PropTypes.array,
  visibleItems: PropTypes.array,
  handleShowAll: PropTypes.func,
  showAll: PropTypes.bool,
  itemType: PropTypes.string,
};

export default function Overview() {
  const [versionOpen, setVersionOpen] = useState(true);
  const params = useParams();
  const [appOverviewData, setAppOverviewData] = useState([]);
  const navigate = useNavigate();
  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [loader, setLoader] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [title, setTitle] = useState("");
  const [versionName, setVersionName] = useState("");
  const [appIcon, setAppIcon] = useState("");
  const [category, setCategory] = useState("");
  const [isOemApp, setIsOemApp] = useState(false);
  const [publishedBy, setPublishedBy] = useState("");
  const [showQAReports, setShowQAReports] = useState([]);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [targetedMakes, setTargetedMakes] = useState([]);
  const [visibleMakes, setVisibleMakes] = useState([]);
  const [targetedCountries, setTargetedCountries] = useState([]);
  const [visibleCountries, setVisibleCountries] = useState([]);
  const [targetedECUTypes, setTargetedECUTypes] = useState([]);
  const [visibleECUTypes, setVisibleECUTypes] = useState([]);
  const [targetedLanguages, setTargetedLanguages] = useState([]);
  const [visibleLanguages, setVisibleLanguages] = useState([]);
  const [qaReports, setQaReports] = useState([]);
  const [showAllQaReportsSection, setShowAllQaReportsSection] = useState(false);
  const [open, setOpen] = useState(false);

  const showTwoQAReports = qaReports.slice(0, 2);
  const handleBackClick = () => {
    navigate("/ui/oem-admin/dashboard");
  };

  const handleShowAllCountries = (showAll) => {
    setShowAll(showAll);
    setVisibleCountries(
      showAll ? targetedCountries : targetedCountries.slice(0, 14)
    );
  };

  const handleShowAllMakes = (showAll) => {
    setShowAll(showAll);
    setVisibleMakes(showAll ? targetedMakes : targetedMakes.slice(0, 14));
  };

  const handleShowAllECUTypes = (showAll) => {
    setShowAll(showAll);
    setVisibleECUTypes(
      showAll ? targetedECUTypes : targetedECUTypes.slice(0, 14)
    );
  };

  const handleShowAllLanguages = (showAll) => {
    setShowAll(showAll);
    setVisibleLanguages(
      showAll ? targetedLanguages : targetedLanguages.slice(0, 14)
    );
  };
  
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setShowAllQaReportsSection(!showAllQaReportsSection);
    setShowQAReports(showTwoQAReports);
  };

  const handleViewMoreReports = (clickShowReportsToggleBoolean) => {
    setShowAllQaReportsSection(clickShowReportsToggleBoolean);
    if (clickShowReportsToggleBoolean) {
      handleOpen();
      setShowQAReports(qaReports);
    } else {
      setShowQAReports(showTwoQAReports);
    }
  };

  const handleVersionToggle = (version, versionCode) => {
    setVersionOpen(!versionOpen);
    setExpandedVersion(expandedVersion === version ? null : version);
    if (expandedVersion !== version) {
      setLoader(true);
      APIManager.getOEMAppBasedOnVersionCode(params.contentID, versionCode)
        .then((response) => {
          setAppOverviewData(response);
          const index = response.findIndex(
            (x) => x.versionCode === versionCode
          );
          responseSettingOverviewData(index, response);
        })
        .catch((error) => {
          setComponentName("appError");
          setErrorMessage(error.message);
          setHeaderMessage(
            error.message === "jwt expired" ? "401 Token Expired" : "Error"
          );
          setOpenErrorPopup(true);
          setLoader(false);
        });
    }
  };

  function responseSettingOverviewData(index, response) {
    setExpandedVersion(response[index].contentVersion);
    setTitle(response[index]?.title || "N/A");
    setVersionName(response[index]?.contentVersion);
    setAppIcon(response[index]?.icon || "N/A");
    setCategory(response[index]?.category || "N/A");
    setIsOemApp(response[index]?.isOemApp || false);
    setPublishedBy(response[index]?.seller?.name || "N/A");
    setTargetedMakes(response[index]?.makes || ["N/A"]);
    setVisibleMakes(
      showAll ? response[index]?.makes : response[index]?.makes.slice(0, 14)
    );
    setTargetedCountries(response[index]?.countries);
    setVisibleCountries(
      showAll
        ? response[index]?.countries
        : response[index]?.countries.slice(0, 14)
    );

    setTargetedECUTypes(response[index]?.ecuTypes || ["N/A"]);
    setVisibleECUTypes(
      showAll
        ? response[index]?.ecuTypes
        : response[index]?.ecuTypes.slice(0, 14)
    );
    setTargetedLanguages(response[index]?.language || ["N/A"]);
    setVisibleLanguages(
      showAll
        ? response[index]?.language
        : response[index]?.language.slice(0, 14)
    );
    setQaReports(response[index]?.qaReports || []);

    setLoader(false);
  }

  useEffect(() => {
    setLoader(true);
    APIManager.getOEMAppOverviewData(params.contentID)
      .then((response) => {
        setAppOverviewData(response);
        responseSettingOverviewData(0, response);
      })
      .catch((error) => {
        setComponentName("appError");
        setErrorMessage(error.message);
        setHeaderMessage(
          error.message === "jwt expired" ? "401 Token Expired" : "Error"
        );
        setOpenErrorPopup(true);
        setLoader(false);
      });
  }, []);

  useEffect(() => {
    if (qaReports.length > 0) {
      const showTwoQAReports = qaReports.slice(0, 2);
      setShowQAReports(showTwoQAReports);
    }
  }, [qaReports]);

  const navigateToApplicationDetails = ($event, index) => {
    let firstUrl = getConfig().REACT_APP_REDIRECT_URI;
    $event.stopPropagation();
    const navigatingURL = `${firstUrl}/${sessionStorage.getItem(
      "portfolioId"
    )}/content/${params.contentID}`;
    if (index === 1) {
      globalThis.location.href = navigatingURL;
    } else {
      globalThis.open(navigatingURL, "_blank");
    }
  };

  const renderStatisticsGrid = (items) => (
    <Grid2 container spacing={10}>
      {items.map((item) => (
        <Grid2 size={4} key={item.label}>
          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary">
              {item.label}
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {item.value}
            </Typography>
          </Box>
        </Grid2>
      ))}
    </Grid2>
  );

  const renderShowMoreButton = (config) => {
    const { onClick, count } = config;
    return (
      <Button
        className="customButton"
        size="small"
        onClick={onClick}
        style={{
          height: "24px",
          width: "100px",
          textTransform: "none",
          color: "rgba(4, 118, 226, 1)",
          border: "1px solid rgba(4, 118, 226, 1)",
          borderRadius: "4px",
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        {count} {" More"}
        {""}
        <svg
          width="8"
          height="10"
          viewBox="0 0 8 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {" "}
          <path
            d="M4 9.33342L0 5.33342L0.933333 4.40008L3.33333 6.80008V0.666748H4.66667V6.80008L7.06667 4.40008L8 5.33342L4 9.33342Z"
            fill="#0476E2"
          />{" "}
        </svg>
      </Button>
    );
  };

  const renderCollapseButton = (onClick) => (
    <Button
      className="customButton"
      size="small"
      onClick={onClick}
      style={{
        height: "24px",
        width: "100px",
        textTransform: "none",
        color: "rgba(4, 118, 226, 1)",
        border: "1px solid rgba(4, 118, 226, 1)",
        borderRadius: "4px",
        display: "flex",
        justifyContent: "space-around",
      }}
    >
      {"Collapse"}{" "}
      <svg
        width="8"
        height="10"
        viewBox="0 0 8 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4 0.666585L0 4.66658L0.933333 5.59992L3.33333 3.19992V9.33325H4.66667V3.19992L7.06667 5.59992L8 4.66658L4 0.666585Z"
          fill="#0476E2"
        />
      </svg>
    </Button>
  );

  const renderAppInfoBox = (icon, title, versionName, isOem) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "rgba(255, 255, 255, 1)",
        gap: "18px",
        marginBottom: "20px",
        height: "80px",
      }}
    >
      <Box
        sx={{
          width: "80px",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <img
          src={icon}
          alt="App Icon"
          width="80px"
          height="80px"
          style={{ borderRadius: "8px" }}
        />
      </Box>
      <Box>
         <Typography variant="h5" fontWeight={versionName ? 550 : 500}>
          {title || "N/A"}
        </Typography>
        {versionName && (
          <Box sx={{ display: "flex", gap: "6px" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {versionName}
            </Typography>
          </Box>
        )}
        {isOem === true && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "40px",
              height: "20px",
              marginTop: versionName ? "0px" : "6px",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: "600px",
                color: "#2A2C2D",
                backgroundColor: "#E8E8E8",
                padding: "2px 4px 2px 4px",
                borderRadius: "4px",
              }}
            >
              OEM
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {loader && <LoadingBackdrop open={loader} />}
      <Topheader />
      {/* Header Navigation */}

      <Box
        sx={{
          paddingTop: "30px",
          paddingBottom: "40px",
          paddingLeft: "80px",
          paddingRight: "80px",
          display: "flex",
          justifyContent: "flex-start",
          marginTop: "3%",
        }}
      >
        <Box
          sx={{
            cursor: "pointer",
            width: "96px",
            height: "36px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
          }}
          onClick={handleBackClick}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.825 9L9.425 14.6L8 16L0 8L8 0L9.425 1.4L3.825 7H16V9H3.825Z"
                fill="#121415"
                fillOpacity="0.6"
              />
            </svg>
          </Box>

          <Typography variant="body1" color="text.secondary">
            Overview
          </Typography>
        </Box>

        <Box
          sx={{
            marginLeft: "30px",
            marginRight: "30px",
            width: "1100px",
          }}
        >
          {/* App Information */}
          {renderAppInfoBox(appIcon, title, null, isOemApp)}
          {/* Main Content */}
          <Paper
            elevation={0}
            sx={{
              // mx: 3,
              mb: 2,
              backgroundColor: "#F3F3F3",
              borderRadius: "4px",
              height: "max-content",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                <Box
                  component="span"
                  color="rgba(90, 91, 92, 1)"
                  fontSize={"14px"}
                >
                  Category:
                </Box>
                <Box component="span" ml={1} fontWeight={500}>
                  {category || "N/A"}
                </Box>
              </Typography>

              <Typography variant="body2">
                <Box component="span" color="text.secondary">
                  Published by:
                </Box>
                <Box component="span" ml={1}>
                  {publishedBy === "Oem Admin" ? "OEM Admin" : publishedBy}
                </Box>
              </Typography>

            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              style={{ display: "none" }}
            >
              <svg
                width="1152"
                height="1"
                viewBox="0 0 1152 1"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line
                  x1="0.5"
                  y1="0.5"
                  x2="1151.5"
                  y2="0.5"
                  stroke="#121415"
                  strokeOpacity="0.2"
                  strokeLinecap="round"
                  strokeDasharray="4 4"
                />
              </svg>
            </Box>
            {/* Statistics and Payments */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                justifyContent: "space-between",
              }}
              style={{ display: "none" }}
            >
              <Box sx={{ width: "50%", pr: 1 }}>
                <Stack direction="row" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "secondary.main",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1,
                    }}
                  >
                    <Box sx={{ display: "none" }}>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M0 18V16L2 14V18H0ZM4 18V12L6 10V18H4ZM8 18V10L10 12.025V18H8ZM12 18V12.025L14 10.025V18H12ZM16 18V8L18 6V18H16ZM0 12.825V10L7 3L11 7L18 0V2.825L11 9.825L7 5.825L0 12.825Z"
                          fill="white"
                        />
                      </svg>
                    </Box>
                  </Box>
                  <Typography variant="subtitle1">Statistics</Typography>
                </Stack>

                <Paper
                  elevation={1}
                  sx={{ bgcolor: "rgba(18, 20, 21, 0.05)", p: 2 }}
                >
                  {renderStatisticsGrid([
                    { label: "No of Downloads", value: "12" },
                    { label: "No of Sessions", value: "12" },
                    { label: "No of Active Vehicles", value: "123" },
                  ])}
                </Paper>
              </Box>

              <Box sx={{ width: "50%", pr: 1 }} style={{ display: "none" }}>
                <Stack direction="row" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "secondary.main",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1,
                    }}
                  >
                    <Box sx={{}}>
                      <svg
                        width="20"
                        height="16"
                        viewBox="0 0 20 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16H2ZM2 8H18V4H2V8Z"
                          fill="white"
                        />
                      </svg>
                    </Box>
                  </Box>
                  <Typography variant="subtitle1">Statistics</Typography>
                </Stack>

                <Paper
                  elevation={1}
                  sx={{ bgcolor: "rgba(18, 20, 21, 0.05)", p: 2 }}
                >
                  {renderStatisticsGrid([
                    { label: "Total Revenue", value: "12" },
                    { label: "Revenue from last 30 days", value: "12" },
                    { label: "Products Offered", value: "123" },
                  ])}
                </Paper>
              </Box>
            </Box>
          </Paper>
          {/* Version Section */}
          {appOverviewData.map((appVersionData) => (
            <Box
              key={appVersionData.versionCode}
              sx={{
                borderRadius: "4px",
                border: "1px solid",
                marginBottom: "16px",
                borderColor: "rgba(18, 20, 21, 0.2)",
              }}
            >
              <Box
                className="version-section"
                sx={{
                  mx: 2,
                  py: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  gap: "32px",
                }}
                onClick={() =>
                  handleVersionToggle(
                    appVersionData.contentVersion,
                    appVersionData.versionCode
                  )
                }
              >
                <Box
                  direction="row"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  width={"25%"}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    onClick={($event) =>
                      navigateToApplicationDetails($event, 1)
                    }
                  >
                    {appVersionData?.contentVersion}
                  </Typography>

                  <OpenInNew
                    fontSize="small"
                    color="action"
                    onClick={($event) =>
                      navigateToApplicationDetails($event, 2)
                    }
                  />
                </Box>
                {expandedVersion !== appVersionData.contentVersion && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "25%",
                      }}
                    >
                      <StatusBadge status={appVersionData?.status} />
                    </Box>

                    <Box
                      direction="row"
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="center"
                      width={"25%"}
                      gap={1}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        Last Modified Date:
                      </Typography>
                      <Typography>
                        {Moment(appVersionData.statusChangeDate).format(
                          "DD MMM YYYY"
                        )}
                      </Typography>
                    </Box>
                  </>
                )}
                <Box>
                  <IconButton size="small">
                    {expandedVersion === appVersionData.contentVersion ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={expandedVersion === appVersionData.contentVersion}>
                <Box className="qaReports-section">
                  <Box
                    sx={{
                      mx: 2,
                      py: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "2px",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      QA Reports
                    </Typography>

                    {qaReports.length > 2 && (
                      <Typography
                        sx={
                          {
                          fontWeight: "500",
                          fontSize: "12px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                          color: "#0476E2",
                        }}
                        onClick={() => {
                          handleViewMoreReports(!showAllQaReportsSection);
                          handleOpen();
                        }}
                      >
                        {" "}
                        View More Reports
                      </Typography>
                    )}
                  </Box>

                  {!showAllQaReportsSection && (
                    <ApplicationQaReportsPopUp qaReports={showQAReports} />
                  )}
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexWrap={"wrap"}
                  mb={2}
                  mx={2}
                  marginTop={1}
                >
                  <Box width={"25%"} mb={1}>
                    <Typography variant="body2" fontWeight={500} mb={1}>
                      Status
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "12px",
                        width: "max-content",
                        color: "rgba(0, 86, 45, 1))",
                        backgroundColor: "rgba(152, 240, 200, 1)",
                        borderRadius: "4px",
                        padding: "5px 8px 5px 8px",
                        fontWeight: "600",
                      }}
                    >
                      {appVersionData?.status
                        ? appVersionData.status.charAt(0).toUpperCase() +
                          appVersionData.status.slice(1).toLowerCase()
                        : "N/A"}
                    </Typography>
                  </Box>
                  <Box width={"25%"}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      API
                    </Typography>
                    <Typography>
                      {appVersionData?.androidApiLevel ||
                        appOverviewData[0]?.androidApiLevel ||
                        "N/A"}
                    </Typography>
                  </Box>

                  <Box width={"25%"}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Last Modified Date
                    </Typography>
                    <Typography>
                      {" "}
                      {Moment(appVersionData.statusChangeDate).format(
                        "DD MMM YYYY"
                      )}
                    </Typography>
                  </Box>
                  <Box width={"25%"}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Published Date
                    </Typography>
                    <Typography>
                      {" "}
                      {Moment(appVersionData.publishDate).format("DD MMM YYYY")}
                    </Typography>
                  </Box>
                </Box>

                <Box className="targeted-section">
                  <TargetedItemsSection
                    title="Targeted Makes"
                    items={targetedMakes}
                    visibleItems={visibleMakes}
                    handleShowAll={handleShowAllMakes}
                    showAll={showAll}
                    itemType="makes"
                  />

                  <TargetedItemsSection
                    title="Targeted Countries"
                    items={targetedCountries}
                    visibleItems={visibleCountries}
                    handleShowAll={handleShowAllCountries}
                    showAll={showAll}
                    itemType="countries"
                  />

                  <Box mb={3}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      ECU Types
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {visibleECUTypes.map((type) => (
                        <Chip
                          sx={{ borderRadius: "2px" }}
                          key={type}
                          label={type}
                          size="small"
                        />
                      ))}{" "}
                      {!showAll && targetedECUTypes.length > 14 &&
                        renderShowMoreButton({
                          onClick: () => handleShowAllECUTypes(true),
                          count: targetedECUTypes.length - 14,
                        })}
                      {showAll && targetedECUTypes.length > 14 &&
                        renderCollapseButton(() => handleShowAllECUTypes(false))}{" "}
                    </Stack>
                  </Box>

                  <Box mb={3}>
                    <Typography variant="body2" fontWeight={600} mb={1}>
                      Language
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {visibleLanguages.map((language) => (
                        <Chip
                          sx={{ borderRadius: "2px" }}
                          key={language}
                          label={language}
                          size="small"
                        />
                      ))}{" "}
                      {!showAll && targetedLanguages.length > 14 &&
                        renderShowMoreButton({
                          onClick: () => handleShowAllLanguages(true),
                          count: targetedLanguages.length - 14,
                        })}
                      {showAll && targetedLanguages.length > 14 &&
                        renderCollapseButton(() => handleShowAllLanguages(false))}{" "}
                    </Stack>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      </Box>

      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={() => setOpenErrorPopup(false)}
      />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box className="qaReportPopUp" sx={{ width: "900px", height: "444px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
          >
            <Typography
              id="modal-modal-title"
              variant="h6"
              fontWeight={"600"}
              marginLeft={"13px"}
            >
              QA Report
            </Typography>
            <IconButton onClick={handleClose} aria-label="Close QA Report modal">
              <CloseIcon />
            </IconButton>
          </div>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",

              backgroundColor: "rgba(255, 255, 255, 1)",
              gap: "18px",
              marginLeft: "13px",
              marginBottom: "20px",
              height: "80px",
            }}
          >
            {renderAppInfoBox(appIcon, title, versionName, isOemApp)}
          </Box>
          <div
            className="qaReports-popup-section"
            style={{ height: "310px", overflowY: "auto" }}
          >
            <ApplicationQaReportsPopUp qaReports={qaReports} />
          </div>
        </Box>
      </Modal>
    </>
  );
}
